import type { Destination, UserConsent, WindowWithAnalyticsAndCookieFirst } from './types'
import { COOKIES_CATEGORIES } from './utils'

export const mapDestinationToCookieFirst: Record<string, string> = {
  // segment tools
  'Segment.io': 'segment',
  'Actions Customerio': 'customer_io',
  'Actions Google Analytic 4': 'google_analytics',
  'Amazon S3': 'amazon_s3',
  'Bing Ads': 'bing_ads',
  'Facebook Conversions API (Actions)': 'facebook_api_actions',
  'Facebook Pixel': 'facebook_pixel',
  'Facebook Pixel Server Side': 'facebook_conversions',
  'Google AdWords New': 'google_ads',
  'Google Enhanced Conversions': 'google_enhanced',
  'Google Tag Manager': 'google_tag_manager',
  Hotjar: 'hotjar',
  'Impact Partnership Cloud': 'impact',
  'LinkedIn Insight Tag': 'linkedin_insight_tag',
  Mixpanel: 'mixpanel',
  'Mixpanel (Actions)': 'mixpanel',
  'Quora Conversion Pixel': 'quora',
  'Tiktok Conversions': 'tiktok_conversions',
  Zapier: 'zapier',

  // Local cookies group
  ...Object.keys(COOKIES_CATEGORIES).reduce(
    (acc, key) => {
      acc[key] = key
      return acc
    },
    {} as Record<string, string>
  ),
}

// Fetch all tools connected/loaded via segment
const fetchDestinations = async (
  writeKey: string,
  cdnHost = 'cdn.segment.com'
): Promise<Destination[]> => {
  const res = await fetch(`https://${cdnHost}/v1/projects/${writeKey}/integrations`)

  if (!res.ok) {
    throw new Error(
      `Failed to fetch integrations for write key ${writeKey}: HTTP ${res.status} ${res.statusText}`
    )
  }
  return res.json()
}

// Load tools conditionally depends on the given consent
const conditionallyLoadAnalytics = (
  destinations: Destination[],
  userConsent: UserConsent,
  writeKey: string
) => {
  const wd = window as WindowWithAnalyticsAndCookieFirst
  const integrations: Record<string, boolean> = {
    All: false,
    'Segment.io': 'segment' in userConsent ? userConsent.segment : false,
  }
  let isAnythingEnabled = false

  if (!userConsent) {
    return
  }

  for (const destination of destinations) {
    const isEnabled = isScriptAllowed(destination.creationName, userConsent)
    if (isEnabled) {
      isAnythingEnabled = true
    }
    integrations[destination.creationName] = isEnabled
  }

  // Reload the page if the trackers have already been initialised so that
  // the user's new preferences can take affect
  if (wd.analytics?.initialized) {
    wd.location.reload()
  }

  // Don't load a.js at all if nothing has been enabled
  if (isAnythingEnabled) {
    wd.analytics.load(writeKey, { integrations })
  }
}

export const isScriptAllowed = (scriptName: string, userConsent: UserConsent) =>
  Boolean(userConsent[mapDestinationToCookieFirst[scriptName]])

export const loadSegmentIntegrations = async (
  userConsent: UserConsent,
  writeKey: string,
  cdnHost?: string
) => {
  const segmentIntegrations = await fetchDestinations(writeKey, cdnHost)

  conditionallyLoadAnalytics(segmentIntegrations, userConsent, writeKey)
}
